import React, { useMemo } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { TanTable } from './TanTable';
import { useHistoryPanelData } from '../hooks/useHistoryPanelData';
import { formatHistoryDateTime } from '../utils/dateUtils';

interface HistoryRecord {
  recno: number;
  dbtable: string;
  keyvalue: string;
  datetime: string;
  userid: string;
  colname: string;
  oldval: string;
  newval: string;
  type: string;
  otherid: string;
}

interface HistoryResponse {
  count: number;
  records: HistoryRecord[];
}

interface HistoryPanelProps {
  /** Full API path to fetch history from. null = panel is empty / not fetching. */
  endpoint: string | null;
  tableDisplayNames?: Record<string, string>;
  columnDisplayNames?: Record<string, string>;
  /** When true, the panel is mounted and visible. Used to defer fetching until
   *  the user actually opens the tab. */
  active?: boolean;
  /** Bump this number to trigger a refetch from the parent. */
  refreshNonce?: number;
  /** Title shown in the table card header. */
  cardTitle?: string;
  /** Per-user settings bucket for TanTable; omit to keep the legacy default. */
  settingsKey?: string;
}

export const HistoryPanel: React.FC<HistoryPanelProps> = ({
  endpoint,
  tableDisplayNames = {},
  columnDisplayNames = {},
  active = true,
  refreshNonce = 0,
  cardTitle = 'Pro History',
  settingsKey,
}) => {
  const { data: historyData, loading, error } = useHistoryPanelData<HistoryResponse>(
    endpoint,
    active,
    refreshNonce,
    'Failed to fetch history'
  );

  const columns = useMemo<ColumnDef<HistoryRecord>[]>(
    () => [
      {
        accessorKey: 'datetime',
        header: 'Date/Time',
        cell: (info) => formatHistoryDateTime(info.getValue() as string),
        size: 160,
      },
      {
        accessorKey: 'userid',
        header: 'User',
        cell: (info) => {
          const record = info.row.original;
          return (
            <div title={record.type || record.userid}>
              {record.userid}
              {record.type && (
                <div className="text-xs text-muted truncate">
                  {record.type}
                </div>
              )}
            </div>
          );
        },
        size: 180,
      },
      {
        accessorKey: 'dbtable',
        header: 'Table',
        cell: (info) => {
          const val = info.getValue() as string;
          return tableDisplayNames[val] ?? val;
        },
        size: 120,
      },
      {
        accessorKey: 'otherid',
        header: 'Other ID',
        size: 80,
      },
      {
        accessorKey: 'colname',
        header: 'Field',
        cell: (info) => {
          const val = info.getValue() as string;
          return columnDisplayNames[val] ?? val;
        },
        size: 140,
      },
      {
        accessorKey: 'oldval',
        header: 'Old Value',
        cell: (info) => (info.getValue() as string) || '-',
        size: 150,
      },
      {
        accessorKey: 'newval',
        header: 'New Value',
        cell: (info) => (info.getValue() as string) || '-',
        size: 150,
      },
    ],
    [tableDisplayNames, columnDisplayNames]
  );

  return (
    <div className="h-full flex flex-col">
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-3">
          <p className="text-red-700 dark:text-red-400 text-sm">{error}</p>
        </div>
      )}
      <div className="flex-1 min-h-[200px]">
        <TanTable
          data={historyData?.records || []}
          columns={columns}
          loading={loading}
          cardTitle={cardTitle}
          searchPlaceholder="Search history..."
          settingsKey={settingsKey}
          fillContainer
          enableSearch
          enableReset
          enableShowHideCols
          enableColumnFilters
          enableZebraStriping={false}
          defaultSorting={[{ id: 'datetime', desc: true }]}
        />
      </div>
    </div>
  );
};

export default HistoryPanel;
